import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, Mail, Briefcase } from "lucide-react";
import { Link } from "react-router-dom";
import {
  PORTFOLIO_PRICING_STATUS_LABELS,
  getPortfolioPricingStatusTone,
  type PortfolioPricingApplicationRow,
  type PortfolioPricingApplicationStatus,
} from "@/lib/portfolioPricing";
import type { ReviewedPricingScheduleAcceptanceRow } from "@/hooks/use-reviewed-pricing-schedule";

type PortfolioPricingStatusCardProps = {
  application: PortfolioPricingApplicationRow;
  compact?: boolean;
  activateDomainHref?: string | null;
  reviewedPricingSchedule?: ReviewedPricingScheduleAcceptanceRow | null;
};

const getStatusCopy = (
  status: PortfolioPricingApplicationRow["status"],
  reviewedPricingSchedule?: ReviewedPricingScheduleAcceptanceRow | null,
) => {
  if (status === "approved" && reviewedPricingSchedule) {
    return {
      title: "Reviewed pricing accepted",
      body: "You have already accepted your reviewed pricing schedule. Future domain activations will use the reviewed pricing path currently configured on your account.",
    };
  }

  if (status === "approved") {
    return {
      title: "Portfolio pricing approved",
      body: "Your reviewed pricing path is approved. You’ll confirm the exact custom rate and billing start terms when you activate a domain.",
    };
  }

  if (status === "declined") {
    return {
      title: "Standard path remains active",
      body: "Your application was reviewed and not approved for portfolio pricing at this time. You can continue using the standard self-serve path, and if your portfolio or use case changes you can submit an updated application.",
    };
  }

  if (status === "under_review") {
    return {
      title: "Portfolio pricing is under review",
      body: "ShadowScan is reviewing whether your account is a fit for portfolio pricing. You can continue with the standard self-serve path while review is in progress.",
    };
  }

  return {
    title: "Portfolio pricing application received",
    body: "Your application has been submitted for review. You can continue with the standard self-serve path while ShadowScan reviews the right pricing path for your account.",
  };
};

export const PortfolioPricingStatusCard = ({
  application,
  compact = false,
  activateDomainHref = null,
  reviewedPricingSchedule = null,
}: PortfolioPricingStatusCardProps) => {
  const copy = getStatusCopy(application.status, reviewedPricingSchedule);
  const badgeVariant = getPortfolioPricingStatusTone(application.status as PortfolioPricingApplicationStatus);
  const hasAcceptedReviewedPricing =
    application.status === "approved" && Boolean(reviewedPricingSchedule?.accepted_at);
  const badgeLabel = hasAcceptedReviewedPricing
    ? "Accepted"
    : PORTFOLIO_PRICING_STATUS_LABELS[application.status];

  return (
    <Card className="border-border/70 bg-card/80">
      <CardHeader className={compact ? "pb-3" : undefined}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-primary" />
              <CardTitle className={compact ? "text-base" : "text-lg"}>{copy.title}</CardTitle>
            </div>
            <CardDescription className="max-w-2xl leading-relaxed text-muted-foreground">
              {copy.body}
            </CardDescription>
          </div>
          <Badge variant={badgeVariant}>{badgeLabel}</Badge>
        </div>
      </CardHeader>
      <CardContent className={compact ? "space-y-2" : "space-y-3"}>
        <div className="text-sm text-muted-foreground">
          Submitted {new Date(application.submitted_at).toLocaleString()}
          {application.reviewed_at ? ` · reviewed ${new Date(application.reviewed_at).toLocaleString()}` : ""}
        </div>
        {hasAcceptedReviewedPricing ? (
          <div className="text-sm text-muted-foreground">
            Reviewed pricing accepted {new Date(reviewedPricingSchedule!.accepted_at).toLocaleString()}
          </div>
        ) : null}
        <div className="text-sm text-muted-foreground">
          {hasAcceptedReviewedPricing
            ? "Your accepted reviewed pricing schedule stays available in Account Settings for reference."
            : "Standard billing still applies unless ShadowScan approves a separate pricing path."}
        </div>
        {application.review_notes ? (
          <div className="rounded-md border border-border/60 bg-background/60 p-3 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Review note:</span> {application.review_notes}
          </div>
        ) : null}
        <div className="flex flex-wrap gap-3">
          {application.status === "approved" && activateDomainHref && !hasAcceptedReviewedPricing ? (
            <Button asChild size="sm">
              <Link to={activateDomainHref}>
                <ArrowRight className="h-4 w-4" />
                Add domain &amp; review pricing
              </Link>
            </Button>
          ) : null}
          <Button asChild variant="outline" size="sm">
            <a href="mailto:support@shadowscan.com.au?subject=ShadowScan%20portfolio%20pricing%20application">
              <Mail className="h-4 w-4" />
              Contact support
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
